/**
 * @file components/log/FoodLogger.jsx
 */

import { Calendar, Camera, CheckCircle2, Info, Pencil, Save, Sparkles, X } from "lucide-react";
import { useRef, useMemo, useState } from "react";
import { useMealMutations } from "../../hooks/useMealMutations";
import { useSubscription } from "../../hooks/useSubscription";
import { trackMealLogged } from "../../services/analytics";
import { trackAIUsage } from "../../services/subscription";
import { trackGuestUsage, trackGuestPhotoTrial } from "../../hooks/useSubscription";
import { calculateUserTargets } from "../../utils/calculateUserTargets";
import ProgressBar from "../shared/ProgressBar";
import { PremiumBadge } from "../shared/PremiumBadge";
import { UpgradeModal } from "../shared/UpgradeModal";
import { SourceBadge } from "./SourceBadge";
import { useProfile } from "../../hooks/useProfile";
import { useAuth } from "../../utils/AuthContext";

export default function FoodLogger({ todayMeals, onShowAuth, onSetupGoals }) {
  const { uid, isGuest } = useAuth();

  const [input, setInput] = useState("");
  const [image, setImage] = useState(null); // { base64, mimeType, preview }
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState(false);
  const [logDate, setLogDate] = useState(() =>
    new Date().toLocaleDateString("en-CA"),
  ); // YYYY-MM-DD, defaults to today
  const [isEditing, setIsEditing] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState("default");
  const [showPhotoOptions, setShowPhotoOptions] = useState(false);

  const MAX_INPUT_LENGTH = 250;
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Destructure all mutations from your central hook
  const { addMealMutation, analyzeMealMutation } = useMealMutations(uid);
  const { data: profileData } = useProfile(uid);
  const { isPremium, remaining, photoTrialsRemaining, refetch: refetchUsage } =
    useSubscription(uid);

  // Map hook states to your UI variables

  const loading = analyzeMealMutation.isPending;
  const saving = addMealMutation.isPending;
  const todayTotal = todayMeals.reduce((s, m) => s + (m.calories || 0), 0);
  const todayPro = todayMeals.reduce((s, m) => s + (m.protein || 0), 0);
  const error =
    analyzeMealMutation.error?.message || addMealMutation.error?.message || "";

  const dynamicTargets = useMemo(
    () => calculateUserTargets(profileData),
    [profileData],
  );

  function handleImageSelect(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type and size (max 5MB)
    if (!file.type.startsWith("image/")) return;
    if (file.size > 5 * 1024 * 1024) return;

    // Resize to max 768px (Gemini's single-tile threshold = 258 tokens minimum)
    const img = new Image();
    img.onload = () => {
      const MAX = 768;
      let { width, height } = img;
      if (width > MAX || height > MAX) {
        const scale = MAX / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);

      // Encode as JPEG at 0.8 quality — smallest base64 payload
      const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
      const base64 = dataUrl.split(",")[1];

      setImage({
        base64,
        mimeType: "image/jpeg",
        preview: dataUrl,
      });
      setSaved(false);
      if (result) { setResult(null); setIsEditing(false); }
      URL.revokeObjectURL(img.src);
    };
    img.src = URL.createObjectURL(file);
  }

  function removeImage() {
    setImage(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  async function handleAnalyse() {
    if (!input.trim() && !image) return;
    setSaved(false);

    // Check tier limits before calling AI
    if (!isPremium) {
      // Guests get a few free photo analyses to experience the "wow" feature.
      const canUsePhotoTrial = isGuest && photoTrialsRemaining > 0;
      if (image && !canUsePhotoTrial) {
        setUpgradeReason("photo_premium");
        setShowUpgrade(true);
        return;
      }
      if (remaining <= 0) {
        setUpgradeReason("limit_reached");
        setShowUpgrade(true);
        return;
      }
    }

    const payload = {
      text: input.trim() || "",
      image: image ? { base64: image.base64, mimeType: image.mimeType } : null,
    };

    analyzeMealMutation.mutate(payload, {
      onSuccess: (res) => {
        setResult(res);
        if (isGuest) {
          trackGuestUsage();
          if (image) trackGuestPhotoTrial();
          refetchUsage();
        } else {
          trackAIUsage(uid, !!image).then(() => refetchUsage());
        }
      },
    });
  }

  async function handleSave() {
    if (!result || !uid) return;

    const mealTitle =
      input.trim() || result.title?.trim() || "Photo Meal";

    // If the user picked a date other than today, log the meal on that date.
    // Otherwise leave `date` undefined so the server timestamp (now) is used.
    const todayStr = new Date().toLocaleDateString("en-CA");
    let date;
    if (logDate && logDate !== todayStr) {
      const picked = new Date(`${logDate}T12:00:00`);
      if (!Number.isNaN(picked.getTime())) date = picked.toISOString();
    }

    addMealMutation.mutate(
      {
        description: mealTitle,
        calories: result.totalCal,
        protein: result.totalPro,
        items: result.items || [],
        source: process.env.REACT_APP_AI_KEY ? "ai" : "local",
        hasImage: !!image,
        ...(date ? { date } : {}),
      },
      {
        onSuccess: () => {
          trackMealLogged(uid, mealTitle, result.totalCal);
          setSaved(true);
          setInput("");
          setImage(null);
          setResult(null);
          setLogDate(todayStr);
          if (fileInputRef.current) fileInputRef.current.value = "";
        },
      },
    );
  }

  const canAnalyze = input.trim() || image;

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 max-w-2xl mx-auto px-4">
      <header className="mb-8 mt-4">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-white tracking-tight">
            Meal Tracker
          </h1>
          <PremiumBadge
            isPremium={isPremium}
            remaining={remaining}
            onUpgrade={() => { setUpgradeReason("default"); setShowUpgrade(true); }}
          />
        </div>
        <p className="text-gray-500 text-sm mt-1 font-medium">
          Describe your meal in plain words — get instant calories & macros.
        </p>
      </header>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10">
        {[
          {
            label: "Calories",
            val: todayTotal,
            unit: "kcal",
            color: "text-amber-400",
            bg: "bg-amber-400/5",
          },
          {
            label: "Protein",
            val: todayPro.toFixed(1),
            unit: "g",
            color: "text-emerald-400",
            bg: "bg-emerald-400/5",
          },
          {
            label: "Entries",
            val: todayMeals?.length || 0,
            unit: "logged",
            color: "text-blue-400",
            bg: "bg-blue-400/5",
          },
        ].map((s, i) => (
          <div
            key={i}
            className={`p-4 rounded-2xl border border-white/5 ${s.bg}`}
          >
            <p className="text-[10px] uppercase font-bold tracking-widest text-gray-500 mb-1">
              {s.label}
            </p>
            <div className="flex items-baseline gap-1">
              <span className={`text-xl font-bold ${s.color}`}>{s.val}</span>
              <span className="text-[10px] text-gray-600 font-medium">
                {s.unit}
              </span>
            </div>
          </div>
        ))}

        <div className="p-4 rounded-2xl border border-white/5 bg-gradient-to-br from-purple-400/5 to-pink-400/5">
          <p className="text-[10px] uppercase font-bold tracking-widest text-gray-500 mb-3">
            Daily Goals
          </p>
          {dynamicTargets?.calories === "--" ? (
            <button
              type="button"
              onClick={() => onSetupGoals?.()}
              style={{ touchAction: "manipulation" }}
              className="w-full flex flex-col items-start gap-0.5 rounded-xl bg-purple-400/10 border border-purple-400/20 px-3 py-2 text-left hover:bg-purple-400/15 transition-all active:scale-[0.98]"
            >
              <span className="text-xs font-bold text-purple-300">
                Set your goal →
              </span>
              <span className="text-[9px] text-gray-500">
                Get your target in seconds
              </span>
            </button>
          ) : (
            <div className="space-y-2">
              <div className="flex items-baseline gap-2">
                <span className="text-base font-bold text-purple-400">
                  {dynamicTargets?.calories}
                </span>
                <span className="text-[8px] text-gray-600 font-medium uppercase">
                  kcal
                </span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-base font-bold text-pink-400">
                  {dynamicTargets?.protein}
                </span>
                <span className="text-[8px] text-gray-600 font-medium uppercase">
                  g pro
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Input Section */}
      <div className="space-y-4 mb-8">
        <label className="text-xs font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
          <Sparkles size={14} className="text-emerald-500" /> Natural Log
        </label>
        <div className="relative group">
          <textarea
            value={input}
            onChange={(e) => {
              const val = e.target.value;
              if (val.length <= MAX_INPUT_LENGTH) {
                setInput(val);
                setSaved(false);
                if (result) { setResult(null); setIsEditing(false); }
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.ctrlKey) handleAnalyse();
            }}
            placeholder="Describe your meal... (e.g., 2 eggs and toast)"
            maxLength={MAX_INPUT_LENGTH}
            rows={4}
            className="w-full bg-[#121212] border border-white/10 rounded-2xl px-5 py-4 text-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500/40 transition-all resize-none shadow-inner"
          />
          <div className="flex justify-end">
            <span className={`text-[10px] font-medium ${input.length >= MAX_INPUT_LENGTH ? 'text-red-400' : 'text-gray-600'}`}>
              {input.length}/{MAX_INPUT_LENGTH}
            </span>
          </div>
        </div>

        {/* Quick-add examples — guide first-time users to the "aha" moment */}
        {!input && !result && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] text-gray-600 font-bold uppercase tracking-widest">
              Try
            </span>
            {[
              "2 eggs and toast",
              "chicken, rice and broccoli",
              "protein shake with banana",
            ].map((example) => (
              <button
                key={example}
                type="button"
                onClick={() => {
                  setInput(example);
                  setSaved(false);
                }}
                style={{ touchAction: "manipulation" }}
                className="px-3 py-1.5 rounded-full bg-white/[0.03] border border-white/10 text-[11px] text-gray-400 hover:border-emerald-500/40 hover:text-emerald-400 transition-all active:scale-95"
              >
                {example}
              </button>
            ))}
          </div>
        )}

        {/* Image Upload / Camera */}
        <div className="relative flex items-center gap-3 flex-wrap">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageSelect}
            className="hidden"
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleImageSelect}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => setShowPhotoOptions(!showPhotoOptions)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-white/10 bg-[#121212] text-gray-300 text-xs font-medium hover:border-emerald-500/40 hover:text-emerald-400 transition-all relative shrink-0"
          >
            <Camera size={16} />
            {image ? "Change Photo" : "Add Photo"}
            {!isPremium && !image && !(isGuest && photoTrialsRemaining > 0) && (
              <span className="absolute -top-1.5 -right-1.5 text-[7px] bg-brand-amber text-black px-1.5 py-0.5 rounded-full font-black uppercase">
                Pro
              </span>
            )}
            {!isPremium && !image && isGuest && photoTrialsRemaining > 0 && (
              <span className="absolute -top-1.5 -right-1.5 text-[7px] bg-brand-green text-black px-1.5 py-0.5 rounded-full font-black uppercase">
                Free
              </span>
            )}
          </button>

          {/* Photo source action sheet */}
          {showPhotoOptions && (
            <div className="absolute top-full left-0 mt-2 z-50 bg-[#1a1a1a] border border-white/10 rounded-xl shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150">
              <button
                onClick={() => { cameraInputRef.current?.click(); setShowPhotoOptions(false); }}
                className="w-full flex items-center gap-3 px-5 py-3 text-xs text-gray-200 hover:bg-white/5 transition-colors text-left"
              >
                <Camera size={14} className="text-emerald-400" />
                Take Photo
              </button>
              <button
                onClick={() => { fileInputRef.current?.click(); setShowPhotoOptions(false); }}
                className="w-full flex items-center gap-3 px-5 py-3 text-xs text-gray-200 hover:bg-white/5 transition-colors text-left border-t border-white/5"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-400"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>
                Photo Library
              </button>
            </div>
          )}

          {!image && !showPhotoOptions && (
            <span className="text-[10px] text-gray-600">
              {isPremium
                ? "Snap or upload a meal photo for better accuracy"
                : isGuest && photoTrialsRemaining > 0
                  ? `${photoTrialsRemaining} free photo ${photoTrialsRemaining === 1 ? "scan" : "scans"} left — try it!`
                  : "Premium feature — upgrade to unlock"}
            </span>
          )}
        </div>

        {/* Image Preview */}
        {image && (
          <div className="relative inline-block">
            <img
              src={image.preview}
              alt="Meal preview"
              className="w-32 h-32 object-cover rounded-xl border border-white/10"
            />
            <button
              type="button"
              onClick={removeImage}
              className="absolute -top-2 -right-2 p-1 rounded-full bg-red-500 text-white hover:bg-red-400 transition-colors"
            >
              <X size={12} />
            </button>
          </div>
        )}

        <button
          onClick={handleAnalyse}
          disabled={loading || !canAnalyze}
          className="w-full py-4 rounded-2xl bg-emerald-500 hover:bg-emerald-400 disabled:bg-gray-800 text-black font-bold text-sm uppercase tracking-widest transition-all active:scale-[0.98]"
        >
          {loading ? "Calculating..." : image ? "Analyze Meal & Photo" : "Analyze Meal"}
        </button>
      </div>

      {/* Results Section */}
      {result && (
        <div className="animate-in zoom-in-95 duration-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest">
              Nutrient Breakdown
            </h3>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsEditing(!isEditing)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all ${
                  isEditing
                    ? "bg-blue-500/20 text-blue-400 border border-blue-500/30"
                    : "bg-white/5 text-gray-500 border border-white/10 hover:text-gray-300"
                }`}
              >
                <Pencil size={10} />
                {isEditing ? "Editing" : "Edit"}
              </button>
              <SourceBadge result={result} />
            </div>
          </div>

          <div className="bg-[#121212] border border-white/10 rounded-2xl overflow-hidden mb-6">
            {result.items?.map((item, i) => (
              <div
                key={i}
                className="flex justify-between items-center p-4 border-b border-white/5 last:border-0 hover:bg-white/[0.02]"
              >
                <div>
                  <p className="text-sm font-medium text-gray-200 capitalize">
                    {item.name}
                  </p>
                  <p className="text-[11px] text-gray-500">
                    {item.per || "Standard portion"}
                  </p>
                </div>
                {isEditing ? (
                  <div className="flex gap-3">
                    <div className="text-center">
                      <input
                        type="number"
                        value={item.cal}
                        onChange={(e) => {
                          const newItems = [...result.items];
                          newItems[i] = { ...newItems[i], cal: Number(e.target.value) || 0 };
                          setResult({
                            ...result,
                            items: newItems,
                            totalCal: newItems.reduce((s, it) => s + (it.cal || 0), 0),
                          });
                        }}
                        className="w-16 bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-sm font-bold text-amber-500 text-center focus:outline-none focus:border-amber-500/40"
                      />
                      <p className="text-[8px] text-gray-600 uppercase font-bold mt-1">Kcal</p>
                    </div>
                    <div className="text-center">
                      <input
                        type="number"
                        value={Math.round(item.pro)}
                        onChange={(e) => {
                          const newItems = [...result.items];
                          newItems[i] = { ...newItems[i], pro: Number(e.target.value) || 0 };
                          setResult({
                            ...result,
                            items: newItems,
                            totalPro: newItems.reduce((s, it) => s + (it.pro || 0), 0),
                          });
                        }}
                        className="w-16 bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-sm font-bold text-emerald-500 text-center focus:outline-none focus:border-emerald-500/40"
                      />
                      <p className="text-[8px] text-gray-600 uppercase font-bold mt-1">Pro</p>
                    </div>
                  </div>
                ) : (
                <div className="flex gap-6 text-right">
                  <div>
                    <p className="text-sm font-bold text-amber-500">
                      {item.cal}
                    </p>
                    <p className="text-[8px] text-gray-600 uppercase font-bold">
                      Kcal
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-emerald-500">
                      {Math.round(item.pro)}g
                    </p>
                    <p className="text-[8px] text-gray-600 uppercase font-bold">
                      Pro
                    </p>
                  </div>
                </div>
                )}
              </div>
            ))}
          </div>

          <div className="p-5 rounded-2xl bg-white/5 border border-white/10 space-y-6">
            <div className="flex gap-10">
              <Metric
                value={result.totalCal}
                label="Calories"
                color="text-amber-500"
              />
              <Metric
                value={`${result.totalPro}g`}
                label="Protein"
                color="text-emerald-500"
              />
            </div>

            <div className="space-y-4">
              {dynamicTargets.calories === "--" ? (
                <button
                  type="button"
                  onClick={() => onSetupGoals?.()}
                  style={{ touchAction: "manipulation" }}
                  className="w-full flex items-center justify-between gap-3 p-3.5 rounded-xl bg-purple-400/10 border border-purple-400/20 hover:bg-purple-400/15 transition-all active:scale-[0.99]"
                >
                  <span className="text-xs font-medium text-purple-200 text-left leading-relaxed">
                    Set a goal to track your daily calorie & protein progress
                  </span>
                  <span className="text-purple-300 text-base font-bold shrink-0">→</span>
                </button>
              ) : (
                <>
                  <ProgressBar
                    value={result.totalCal}
                    max={dynamicTargets.calories}
                    label="Day's Calorie Impact"
                    unit=" kcal"
                  />
                  <ProgressBar
                    value={result.totalPro}
                    max={dynamicTargets.protein}
                    label="Day's Protein Progress"
                    unit="g"
                  />
                </>
              )}
            </div>

            <div className="flex items-start gap-3 p-3 bg-white/5 rounded-xl border border-white/5">
              <Info size={16} className="text-gray-400 shrink-0 mt-0.5" />
              <p className="text-[11px] text-gray-400 leading-relaxed italic">
                {dynamicTargets.calories === "--"
                  ? "Nice — set a goal above to see how this fits into your day."
                  : result.totalCal > dynamicTargets.calories * 0.55
                    ? "This is a heavy intake. Balance your next meal with greens."
                    : "Balanced entry. Great pace toward your goal."}
              </p>
            </div>

            {saved ? (
              <div className="flex items-center justify-center gap-2 text-emerald-400 font-bold text-sm bg-emerald-400/10 py-4 rounded-xl border border-emerald-400/20">
                <CheckCircle2 size={18} /> Logged Successfully
              </div>
            ) : (
              <>
                {/* Optional date — defaults to today, change to log on another day */}
                <label className="flex items-center justify-between gap-3 p-3.5 rounded-xl bg-white/5 border border-white/10">
                  <span className="flex items-center gap-2 text-[11px] font-bold text-gray-400 uppercase tracking-widest">
                    <Calendar size={14} className="text-emerald-500" /> Log Date
                  </span>
                  <input
                    type="date"
                    value={logDate}
                    max={new Date().toLocaleDateString("en-CA")}
                    onChange={(e) =>
                      setLogDate(
                        e.target.value ||
                          new Date().toLocaleDateString("en-CA"),
                      )
                    }
                    className="bg-transparent text-sm font-semibold text-gray-200 focus:outline-none [color-scheme:dark]"
                  />
                </label>

                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="w-full flex items-center justify-center gap-2 py-4 rounded-xl border border-emerald-500/30 text-emerald-400 font-bold text-xs uppercase tracking-widest hover:bg-emerald-500/10 transition-all"
                >
                  <Save size={16} />{" "}
                  {saving ? "Saving..." : "Confirm & Save Entry"}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {error && (
        <p className="mt-4 p-4 text-xs text-red-400 bg-red-400/10 rounded-xl border border-red-400/20 text-center">
          {error}
        </p>
      )}

      {/* Upgrade Modal */}
      {showUpgrade && (
        <UpgradeModal
          reason={upgradeReason}
          onClose={() => setShowUpgrade(false)}
          onShowAuth={onShowAuth}
        />
      )}
    </div>
  );
}

function Metric({ value, label, color }) {
  return (
    <div>
      <p className={`text-3xl font-black tracking-tight ${color}`}>{value}</p>
      <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mt-1">
        {label}
      </p>
    </div>
  );
}
